'use client'

import { useEffect, useState } from 'react'
import Loader from '../loader'

export interface ProgressStep {
  /** Show this label once the wait has lasted at least this many ms. */
  at: number
  label: string
}

/**
 * The overlay a lead sees while we book their meeting.
 *
 * It replaces a bare spinner behind a black scrim, which said nothing for the
 * seconds it was up - long enough on a phone for the lead to wonder whether
 * the tap registered and press again. This one names the step actually in
 * progress and keeps the slot they chose in front of them, so the wait reads
 * as work being done on their behalf rather than as a stall.
 *
 * The labels are time-driven rather than tied to real server milestones: the
 * request is a single round trip, so there is nothing finer to report. They
 * are ordered to match what the server genuinely does in that window, and the
 * last one holds if the wait runs long - it never claims to be finished.
 *
 * Nothing renders for the first `delayMs`. A fast response should flash no
 * spinner at all, which is why the fixed 100ms "make sure the loader is seen"
 * sleep in the calendar fetch could go.
 */
export default function BookingProgress({
  steps,
  isRtl,
  title,
  detail,
  footnote,
  delayMs = 250,
}: {
  steps: ProgressStep[]
  isRtl: boolean
  /** Headline above the status line, e.g. the lead's name. */
  title?: string
  /** The slot being booked, already formatted for display. */
  detail?: string
  footnote?: string
  delayMs?: number
}) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const startedAt = Date.now()
    const tick = setInterval(() => setElapsed(Date.now() - startedAt), 100)
    return () => clearInterval(tick)
  }, [])

  if (elapsed < delayMs) return null

  const current = steps.filter((s) => elapsed >= s.at).pop() || steps[0]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#12102a]/95 p-8 shadow-2xl text-center">
        <div className="flex justify-center">
          <Loader type="brand" size={48} />
        </div>

        {title && (
          <p className="mt-6 text-lg font-semibold text-white">{title}</p>
        )}

        {/* aria-live so a screen reader hears each step, not just the first. */}
        <p
          key={current.label}
          aria-live="polite"
          className="mt-2 text-[0.95rem] text-white/90 motion-safe:animate-[fadeInUp_0.35s_ease-out]"
        >
          {current.label}
        </p>

        {detail && (
          <p className="mt-5 rounded-xl border border-[#24D56D]/25 bg-[#24D56D]/10 px-4 py-3 text-sm font-medium text-[#24D56D]">
            {detail}
          </p>
        )}

        {footnote && <p className="mt-5 text-xs text-white/45">{footnote}</p>}
      </div>
    </div>
  )
}
